import { Component, EventEmitter, Inject, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationFacade, PurchaseService } from '@ridango/components';
import {
    CategoryProduct,
    PeriodType,
    Preferences,
    ProductArea,
    ProductGroup,
    ProductCode,
    Price,
    ParentProductGroup
} from '@ridango/components/lib/purchase/purchase.model';
import { AppStartupService, BaseComponent, TravelMedia } from '@ridango/core';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { Observable, combineLatest, of } from 'rxjs';
import Env from '@ridango/core/env';
import { TicketDurationDescriptionService } from '@ridango/core/lib/services/ticket-duration-description.service';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';

@Component({
    selector: 'gl-purchase-v2-ticket-category-product',
    templateUrl: './category-product.component.html',
    styleUrls: ['./category-product.component.scss']
})
export class CategoryProductComponent extends BaseComponent implements OnInit, OnDestroy {
    @Output() nextStep: EventEmitter<void> = new EventEmitter<void>();
    @Output() errorEvent = new EventEmitter<string>();
    @Input() productAreas: ProductArea[];

    public assetsPath$ = this.applicationFacade.assetsPath$;
    public isLoading$ = this.purchaseService.isPurchaseDataLoading$;
    public ProductCode = ProductCode;

    public productsGroups: ProductGroup[];
    public parentProductGroups: ParentProductGroup[];
    public useNewAfcGrouping = false;
    @Input() selectedProductId: number | string;
    public chosenAreasFromPreviousStep: ProductArea[];
    public currentLanguage = this.translate.currentLang;
    public selectedPreference: Preferences;
    readonly hideDescription = this.env.PURCHASE_FLOW?.HIDE_PRODUCT_CATEGORY_DESCRIPTION;
    public periodTypesList: PeriodType[] = [];
    currency = this.appStartupService.getCurrency();
    public noProductsFound = false;
    private readonly PRODUCT_GROUP_PREFIX = 'Product group with id';

    constructor(
        private applicationFacade: ApplicationFacade,
        private purchaseService: PurchaseService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private translate: TranslateService,
        public appStartupService: AppStartupService,
        public ticketDurationDescriptionService: TicketDurationDescriptionService,
        @Inject('ENV') public env: Env,
        private analyticsService: AnalyticsService
    ) {
        super();
    }

    ngOnInit() {
        this.purchaseService.setPurchaseDataLoading(true);
        const areaId = this.purchaseService.getStepDataIdsByStepName('areas')?.[0] ?? null;
        const productAreaIds = this.purchaseService.getStepDataIdsByStepName('product-area');

        if (this.env.PURCHASE_FLOW.TICKET_STEPS.includes('preferences')) {
            this.purchaseService.currentPreference.pipe(takeUntil(this.componentDestroyed$)).subscribe((preference) => {
                this.selectedPreference = preference;
            });
            if (!this.selectedPreference) {
                return this.errorEvent.emit('NO_MEDIA_SELECTED');
            }
        }
        this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            this.currentLanguage = event.lang;
        });

        if (this.env.IS_NEW_AFC) {
            this.purchaseService
                .fetchPeriodTypes(productAreaIds[0])
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((data: PeriodType[]) => {
                    this.periodTypesList = data;
                });
        }

        this.loadProductCategories(productAreaIds, areaId);
        this.chosenAreasFromPreviousStep = this.productAreas?.filter((pa: ProductArea) => {
            return typeof pa.id === 'string' && productAreaIds.length === 1
                ? decodeURIComponent(productAreaIds) === pa.id
                : productAreaIds.includes(pa.id);
        });
    }

    public async selectProductGroup(productId: number | string, productType?: string) {
        this.analyticsService.eventTrack(TrackedEventAction.SELECTED, {
            category: TrackedEventCategory.TICKETS,
            name: `${this.PRODUCT_GROUP_PREFIX} ${productId}`
        });

        const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        if (this.selectedProductId) {
            urlSegments.pop();
        }
        this.selectedProductId = productId;
        const paths: Array<string | number> = [this.translate.currentLang, 'purchase-v2', ...urlSegments];
        if (productType) {
            paths.push(productType);
        }
        paths.push(this.selectedProductId);
        await this.router.navigate(paths);
        this.nextStep.emit();
    }

    selectBaseFareGroup(baseFareCode: string) {
        const currentUrl = this.router.url;
        const urlSegments = currentUrl.split('/');
        const categoryProductIndex = urlSegments.findIndex((segment) => segment === 'category-product');
        if (categoryProductIndex !== -1) {
            urlSegments[categoryProductIndex + 1] = `fareProduct:${baseFareCode}`;
            if (!urlSegments.includes('product')) {
                urlSegments.push('product');
            }
            const newUrl = urlSegments.join('/');
            this.router.navigateByUrl(newUrl);
        }
    }

    public getPriceByTimeInterval(str: string) {
        const filterProducts = this.getFilteredProductsByPeriodCode(str);

        return filterProducts?.price.amount || 0;
    }

    public getCurrencyByTimeInterval(str: string) {
        const filterProducts = this.getFilteredProductsByPeriodCode(str);

        return filterProducts?.price.currency || this.appStartupService.getCurrency();
    }

    private loadProductCategories(productAreaIds: string[], areaId: string): void {
        this.purchaseService
            .getCategoryProducts(productAreaIds[0], this.selectedPreference?.code)
            .pipe(
                switchMap((categories: CategoryProduct[]) => {
                    return this.loadProductsForCategories(categories, productAreaIds, areaId);
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(
                (categoriesWithProducts: CategoryProduct[]) => {
                    this.createProductGroups(categoriesWithProducts);
                },
                (error) => {
                    this.errorEvent.emit(error.message);
                    console.error(error);
                    this.purchaseService.setPurchaseDataLoading(false);
                }
            );
    }

    private loadProductsForCategories(
        categories: CategoryProduct[],
        productAreaIds: string[],
        areaId: string
    ): Observable<CategoryProduct[]> {
        if (categories.length === 0) {
            return of([]);
        }

        return combineLatest(
            categories.map((category: CategoryProduct) => {
                const filterIds = [];

                if (this.selectedPreference?.id && this.selectedPreference?.code !== TravelMedia.bank_card) {
                    filterIds.push(this.selectedPreference.id.toString());
                } else if (productAreaIds.length > 0) {
                    filterIds.push(productAreaIds[0]);
                }

                if (areaId) {
                    filterIds.push(areaId);
                }

                filterIds.push(category.id.toString());

                return this.purchaseService
                    .getProducts(
                        filterIds.filter(Boolean),
                        this.selectedPreference?.code,
                        this.selectedPreference?.value
                    )
                    .pipe(
                        map((products) => {
                            return this.calculateCategoryPrice(category, products);
                        }),
                        catchError((error) => {
                            console.error(`Error loading products for ${category.code}:`, error);
                            return of({ ...category, products: [], price: this.createEmptyPrice() });
                        })
                    );
            })
        ).pipe(map((categories) => categories.filter((c) => c.products?.length)));
    }

    private calculateCategoryPrice(category: CategoryProduct, products: any[]): CategoryProduct {
        try {
            const hasDynamicLength = products.some((product) => product?.hasDynamicLength === true);
            const price = products.length > 0 ? this.getMaxPriceFromProducts(products) : this.createEmptyPrice();

            return {
                ...category,
                products: products,
                price: price,
                dynamicLength: hasDynamicLength
            };
        } catch (error) {
            console.error(`Error calculating price for ${category.code}:`, error);
            return {
                ...category,
                products: products,
                price: this.createEmptyPrice(),
                dynamicLength: false
            };
        }
    }

    private getMaxPriceFromProducts(products: any[]): Price {
        const validPrices = products
            .map((product) => {
                if (!product) {
                    return this.createEmptyPrice();
                }
                try {
                    return this.purchaseService.definedProductPrice(product);
                } catch (priceError) {
                    console.error('Error calculating product price:', priceError, product);
                    return this.getProductFallbackPrice(product);
                }
            })
            .filter((price) => price && price.amount != null);

        if (validPrices.length === 0) {
            return this.createEmptyPrice();
        }

        return validPrices.reduce((max, currentPrice) => {
            return currentPrice.amount > max.amount ? currentPrice : max;
        });
    }

    private getProductFallbackPrice(product: any): Price {
        if (!product) {
            return this.createEmptyPrice();
        }

        if (product.price) {
            if (product.price.minAmount) {
                return {
                    amount: product.price.minAmount,
                    currency: product.price.currency || 'SEK',
                    maxAmount: product.price.maxAmount || null,
                    minAmount: product.price.minAmount,
                    vatPercent: product.price.vatPercent || 0,
                    vatAmount: product.price.vatAmount || 0,
                    discountedAmount: product.price.discountedAmount || 0,
                    discountAmount: product.price.discountAmount || 0,
                    discountPerMille: product.price.discountPerMille || 0
                };
            }
            if (product.price.amount) {
                return product.price;
            }
        }

        return this.createEmptyPrice();
    }

    private createEmptyPrice(): Price {
        return {
            amount: 0,
            currency: 'SEK',
            maxAmount: null,
            minAmount: null,
            vatPercent: 0,
            vatAmount: 0,
            discountedAmount: 0,
            discountAmount: 0,
            discountPerMille: 0
        };
    }

    private createProductGroups(categoriesWithProducts: CategoryProduct[]): void {
        const productGroups: ProductGroup[] = categoriesWithProducts
            .filter((category) => category.products && category.products.length > 0)
            .map((category) => ({
                code: category.code as any,
                products: [category]
            }));

        // Detect if products have baseFareProduct field (new AFC with baseFareProduct grouping)
        const hasBaseFareProducts = categoriesWithProducts.some((category) =>
            category.products?.some((product) => product.baseFareProduct)
        );

        // Create parent product groups only if IS_NEW_AFC is enabled AND products have baseFareProduct
        // For old AFC clients without baseFareProduct, use the legacy grouping
        if (this.env.IS_NEW_AFC && hasBaseFareProducts) {
            this.useNewAfcGrouping = true;
            this.parentProductGroups = this.purchaseService.createParentProductGroups(productGroups);
        } else {
            this.useNewAfcGrouping = false;
            // Use legacy grouping (groupByCode logic for old AFC)
            this.productsGroups = this.groupByCode(categoriesWithProducts);
        }

        if (
            (this.useNewAfcGrouping && (!this.parentProductGroups || this.parentProductGroups.length === 0)) ||
            (!this.useNewAfcGrouping && (!this.productsGroups || this.productsGroups.length === 0))
        ) {
            this.noProductsFound = true;
        }
        this.purchaseService.setPurchaseDataLoading(false);
    }

    private groupByCode(products: CategoryProduct[]): ProductGroup[] {
        const groupedProduct = Object.values(
            products.reduce((result, { code, ...item }) => {
                if (item.products !== null) {
                    (result[code] = result[code] || { code: code.toUpperCase(), products: [] }).products.push(item);
                }
                return result;
            }, {})
        ) as ProductGroup[];

        const singleTicketGroupIndex = groupedProduct.findIndex((group) => group.code === 'SINGLE');
        if (singleTicketGroupIndex !== -1) {
            const singleTicketGroup = groupedProduct[singleTicketGroupIndex];
            return [
                singleTicketGroup,
                ...groupedProduct.slice(0, singleTicketGroupIndex),
                ...groupedProduct.slice(singleTicketGroupIndex + 1)
            ];
        }

        return groupedProduct;
    }

    private getFilteredProductsByPeriodCode(str: string) {
        const price = !this.env.IS_NEW_AFC ? (str.includes('24') ? '24' : '30') : str.match(/\d+/)[0];
        return this.productsGroups[0].products[0].products.find((item) =>
            item.name.translations[0].translation.includes(price)
        );
    }
}
